import { isTimePreference, type TimePreference } from "./time-preference";

/**
 * One visit date the patient named in the WhatsApp form, with the time of day they
 * asked for. fisiohome-backend writes these paired — one array, never two — so the
 * count of dates and the count of times cannot desync; see
 * internal/dto/appointment_draft/from_text.go (PreferredSchedule).
 */
export interface PreferredSchedule {
	/** Date-only "YYYY-MM-DD", never an instant. */
	date: string;
	/** Null when the patient left the time blank or wrote something unrecognised. */
	timePreference: TimePreference | null;
}

// Date-only, and nothing else: an instant here would already have been read through a
// timezone by whoever wrote it, which is the shift this whole module exists to avoid.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// The backend validates `max=4`. Mirrored rather than trusted: form_data is a
// schemaless JSONB blob and every entry here becomes a row on screen.
const MAX_PREFERRED_SCHEDULES = 4;

/**
 * The candidate dates in a Draft's appointmentScheduling. Empty for every other Draft —
 * only the WhatsApp and customer-portal paths collect them.
 */
export function preferredSchedules(
	scheduling: Record<string, any> | null | undefined,
): PreferredSchedule[] {
	const raw = scheduling?.preferredSchedules;
	if (!Array.isArray(raw)) return [];

	// Filtered before it is clamped, not after: a malformed entry that ate one of the
	// slots would hide a date the patient actually named, which is the one thing this
	// list must not do.
	return raw
		.filter((entry: any) => DATE_ONLY.test(entry?.date))
		.slice(0, MAX_PREFERRED_SCHEDULES)
		.map((entry: any) => ({
			date: entry.date as string,
			timePreference: isTimePreference(entry?.timePreference)
				? entry.timePreference
				: null,
		}));
}

/**
 * A candidate's date as a Date, for formatting only — midnight local, no time of day,
 * because the patient named a day and nothing finer.
 *
 * Built from the date parts rather than parsed, because `new Date("2026-08-03")` is an
 * ISO *instant* — UTC midnight — and rendering that anywhere west of UTC shows the day
 * before. The parts go through the local-time constructor instead, so the admin reads
 * the day the patient wrote whatever the browser's zone is.
 */
export function preferredScheduleDate(date: string): Date {
	const [year, month, day] = date.split("-").map(Number);
	return new Date(year, month - 1, day);
}
